<template>
    <div class="p-6 bg-gray-50 min-h-screen" v-if="hasAccess">
        <!-- Game Plan Tiles -->
        <div class="max-w-7xl mx-auto mb-8 print:hidden">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-bold text-gray-800">
                    Available Game Plans
                </h2>
                <button
                    @click="createNewPlan"
                    class="
                        bg-primary
                        text-white
                        px-4
                        py-2
                        rounded-md
                        shadow-sm
                        hover:bg-primary-dark
                        transition-colors
                        font-bold
                        text-sm
                    "
                >
                    + Create New Plan
                </button>
            </div>
            <div
                v-if="plansList.length === 0"
                class="
                    bg-white
                    p-8
                    text-center
                    rounded-lg
                    border border-dashed border-gray-300
                    text-gray-500
                    italic
                "
            >
                No game plans created yet.
            </div>
            <div
                class="
                    grid grid-cols-1
                    sm:grid-cols-2
                    md:grid-cols-3
                    lg:grid-cols-4
                    gap-4
                "
            >
                <div
                    v-for="p in plansList"
                    :key="p.id"
                    @click="loadPlan(p)"
                    class="
                        bg-white
                        p-4
                        rounded-lg
                        shadow-sm
                        border
                        cursor-pointer
                        hover:border-primary
                        transition-all
                        group
                        relative
                    "
                    :class="[
                        currentPlanId === p.id
                            ? 'border-green-500 ring-2 ring-green-500/20 bg-green-50/60'
                            : 'border-gray-200',
                        isToday(p.game_date) && currentPlanId !== p.id
                            ? 'bg-blue-50/50'
                            : '',
                    ]"
                >
                    <div
                        class="font-bold truncate"
                        :class="
                            isToday(p.game_date)
                                ? 'text-primary'
                                : 'text-gray-800'
                        "
                    >
                        {{ p.opponent || "Unknown Opponent" }}
                    </div>
                    <div
                        class="text-sm flex items-center gap-2 mt-1"
                        :class="
                            isToday(p.game_date)
                                ? 'text-primary font-bold'
                                : 'text-gray-500'
                        "
                    >
                        {{
                            p.game_date
                                ? getLocalDateString(p.game_date)
                                : "No date"
                        }}
                        <span
                            v-if="isToday(p.game_date)"
                            class="
                                bg-primary
                                text-white text-[9px]
                                px-1.5
                                py-0.5
                                rounded-full
                                tracking-wider
                                uppercase
                            "
                            >Today</span
                        >
                    </div>
                    <div
                        class="
                            mt-2
                            text-xs
                            inline-block
                            px-2
                            py-0.5
                            rounded
                            uppercase
                            font-bold
                        "
                        :class="
                            isToday(p.game_date)
                                ? 'bg-primary/10 text-primary'
                                : 'bg-gray-100 text-gray-600'
                        "
                    >
                        {{ p.plan_type }}
                    </div>

                    <button
                        @click.stop="deletePlan(p.id)"
                        class="
                            absolute
                            top-2
                            right-2
                            text-red-400
                            hover:text-red-600
                            opacity-0
                            group-hover:opacity-100
                            transition-opacity
                        "
                    >
                        <ion-icon name="trash-outline"></ion-icon>
                    </button>
                </div>
            </div>

            <div
                v-if="deprecatedPlans.length > 0 && canManageAccess"
                class="mt-6"
            >
                <button
                    @click="showDeprecatedModal = true"
                    class="
                        text-sm text-gray-500
                        hover:text-primary
                        font-medium
                        flex
                        items-center
                        gap-1
                    "
                >
                    <ion-icon name="refresh-outline"></ion-icon> View & Restore
                    Deprecated Plans ({{ deprecatedPlans.length }})
                </button>
            </div>
        </div>

        <!-- Global Access Control Settings (Only for Owner/Admin) -->
        <div
            v-if="canManageAccess"
            class="
                mx-auto
                bg-white
                shadow-lg
                rounded-lg
                overflow-hidden
                border border-gray-200
                mb-6
                print:hidden
            "
        >
            <div
                class="
                    bg-gray-100
                    px-6
                    py-3
                    border-b
                    flex
                    justify-between
                    items-center
                    cursor-pointer
                    hover:bg-gray-200
                    transition-colors
                "
                @click="isAccessControlOpen = !isAccessControlOpen"
            >
                <div class="flex items-center gap-2">
                    <ion-icon
                        :name="
                            isAccessControlOpen
                                ? 'chevron-down-outline'
                                : 'chevron-forward-outline'
                        "
                        class="text-gray-500"
                    ></ion-icon>
                    <h2 class="text-md font-bold text-gray-800">
                        Global Game Plan Access Control
                    </h2>
                </div>
                <button
                    @click.stop="saveGlobalAccess"
                    class="
                        bg-primary
                        text-white
                        px-4
                        py-1.5
                        rounded
                        text-xs
                        font-bold
                        hover:bg-primary-dark
                        transition-colors
                        flex
                        items-center
                        gap-1
                    "
                >
                    <ion-icon name="save-outline"></ion-icon>
                    Save Permissions
                </button>
            </div>
            <div class="p-6" v-show="isAccessControlOpen">
                <p class="text-sm text-gray-600 mb-4">
                    Select the roles (via tags) that are allowed to view Game
                    Plans. Owners and Coaches always have access.
                </p>
                <div class="flex flex-wrap gap-2">
                    <div
                        v-for="tag in availableTags"
                        :key="tag.id"
                        class="
                            text-xs
                            p-2
                            rounded-full
                            inline-flex
                            items-center
                            w-auto
                            gap-2
                            cursor-pointer
                            border
                        "
                        :class="
                            globalActiveTags[tag.id]
                                ? 'opacity-100 border-gray-400 shadow-sm'
                                : 'opacity-50 border-transparent'
                        "
                        :style="{
                            background: tag.color,
                            color: isDark(tag.color) ? '#fff' : '#000',
                        }"
                        @click="
                            globalActiveTags[tag.id] = !globalActiveTags[tag.id]
                        "
                    >
                        <img
                            v-if="tag.icon_type === 'upload'"
                            :src="tag.icon"
                            class="w-[15px] h-[15px]"
                        />
                        <div v-if="tag.icon_type === 'emoji'">
                            {{ tag.icon }}
                        </div>
                        <ion-icon
                            v-if="tag.icon_type === 'ionicons'"
                            :name="tag.icon"
                        ></ion-icon>
                        <span>{{ tag.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div
            id="printable-plan"
            class="
                max-w-7xl
                mx-auto
                bg-white
                shadow-lg
                rounded-lg
                overflow-hidden
                border border-gray-200
            "
        >
            <!-- Header Section -->
            <div class="p-6 border-b border-gray-200 bg-gray-50">
                <div class="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-800">
                            {{ team.name }} Game Plan
                        </h1>
                        <p
                            class="
                                text-sm text-gray-500
                                uppercase
                                tracking-wider
                            "
                        >
                            {{ planType }} Plan
                        </p>
                        <!-- Connected Collaborators -->
                        <div
                            v-if="connectedUsers.length > 0"
                            class="flex items-center gap-1.5 mt-2 print:hidden"
                        >
                            <span
                                class="
                                    inline-block
                                    w-2.5
                                    h-2.5
                                    rounded-full
                                    bg-green-500
                                    animate-pulse
                                "
                            ></span>
                            <span class="text-xs text-gray-500 font-medium"
                                >Collaborating:</span
                            >
                            <div class="flex -space-x-1 overflow-hidden">
                                <span
                                    v-for="(user, uIdx) in connectedUsers"
                                    :key="uIdx"
                                    class="
                                        inline-block
                                        text-[10px]
                                        font-bold
                                        px-2
                                        py-0.5
                                        rounded-full
                                        border border-white
                                        shadow-sm
                                        text-white
                                    "
                                    :style="{
                                        backgroundColor:
                                            user.color || '#4B5563',
                                    }"
                                    :title="user.name"
                                >
                                    {{ user.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="flex gap-4 items-end">
                        <button
                            @click="exportToExcel"
                            class="
                                bg-green-600
                                text-white
                                px-4
                                py-2
                                rounded-md
                                shadow-sm
                                hover:bg-green-700
                                transition-colors
                                font-bold
                                text-sm
                                print:hidden
                            "
                        >
                            <ion-icon
                                name="download-outline"
                                class="text-lg mr-1 align-middle"
                            ></ion-icon>
                            Excel
                        </button>
                        <button
                            @click="printPlan"
                            class="
                                bg-gray-200
                                text-gray-700
                                px-4
                                py-2
                                rounded-md
                                shadow-sm
                                hover:bg-gray-300
                                transition-colors
                                font-bold
                                text-sm
                                print:hidden
                            "
                        >
                            <ion-icon
                                name="print-outline"
                                class="text-lg mr-1 align-middle"
                            ></ion-icon>
                            Print
                        </button>
                        <div class="flex flex-col">
                            <label
                                class="
                                    text-xs
                                    font-bold
                                    text-gray-600
                                    uppercase
                                "
                                >Type</label
                            >
                            <select
                                v-model="planType"
                                @change="updatePlanType($event.target.value)"
                                @focus="onFieldFocus('planType')"
                                @blur="onFieldBlur"
                                :style="
                                    fieldFocusMap['planType']
                                        ? {
                                              borderColor:
                                                  fieldFocusMap['planType']
                                                      .color,
                                              boxShadow:
                                                  '0 0 0 2px ' +
                                                  fieldFocusMap['planType']
                                                      .color +
                                                  '33',
                                          }
                                        : {}
                                "
                                :title="
                                    fieldFocusMap['planType']
                                        ? fieldFocusMap['planType'].name +
                                          ' is editing this field'
                                        : ''
                                "
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                    font-bold
                                "
                            >
                                <option value="Offensive">Offensive</option>
                                <option value="Defensive">Defensive</option>
                            </select>
                        </div>
                        <div class="flex flex-col">
                            <label
                                class="
                                    text-xs
                                    font-bold
                                    text-gray-600
                                    uppercase
                                "
                                >Opponent</label
                            >
                            <input
                                v-model="opponent"
                                @input="updateOpponent($event.target.value)"
                                @focus="onFieldFocus('opponent')"
                                @blur="onFieldBlur"
                                :style="
                                    fieldFocusMap['opponent']
                                        ? {
                                              borderColor:
                                                  fieldFocusMap['opponent']
                                                      .color,
                                              boxShadow:
                                                  '0 0 0 2px ' +
                                                  fieldFocusMap['opponent']
                                                      .color +
                                                  '33',
                                          }
                                        : {}
                                "
                                :title="
                                    fieldFocusMap['opponent']
                                        ? fieldFocusMap['opponent'].name +
                                          ' is editing this field'
                                        : ''
                                "
                                type="text"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                "
                                placeholder="e.g. Eagles"
                            />
                        </div>
                        <div class="flex flex-col">
                            <label
                                class="text-xs font-bold uppercase"
                                :class="
                                    isToday(gameDate)
                                        ? 'text-primary'
                                        : 'text-gray-600'
                                "
                                >Game Date
                                <span
                                    v-if="isToday(gameDate)"
                                    class="
                                        bg-primary
                                        text-white
                                        px-1.5
                                        py-0.5
                                        rounded-full
                                        text-[9px]
                                        ml-1
                                    "
                                    >TODAY</span
                                >
                            </label>
                            <input
                                v-model="gameDate"
                                @change="updateGameDate($event.target.value)"
                                @focus="onFieldFocus('gameDate')"
                                @blur="onFieldBlur"
                                :style="
                                    fieldFocusMap['gameDate']
                                        ? {
                                              borderColor:
                                                  fieldFocusMap['gameDate']
                                                      .color,
                                              boxShadow:
                                                  '0 0 0 2px ' +
                                                  fieldFocusMap['gameDate']
                                                      .color +
                                                  '33',
                                          }
                                        : {}
                                "
                                :title="
                                    fieldFocusMap['gameDate']
                                        ? fieldFocusMap['gameDate'].name +
                                          ' is editing this field'
                                        : ''
                                "
                                type="date"
                                class="
                                    border-gray-300
                                    rounded-md
                                    shadow-sm
                                    focus:border-primary
                                    focus:ring
                                    focus:ring-primary
                                    focus:ring-opacity-50
                                "
                                :class="{
                                    'bg-blue-50 text-primary font-bold border-primary ring-1 ring-primary':
                                        isToday(gameDate),
                                }"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sections -->
            <div
                v-for="(section, sIndex) in sections"
                :key="sIndex"
                class="border-b border-gray-200"
            >
                <div
                    class="
                        bg-gray-800
                        text-white
                        px-4
                        py-2
                        flex
                        justify-between
                        items-center
                        group
                    "
                >
                    <input
                        v-model="section.title"
                        @input="updateSectionTitle(sIndex, $event.target.value)"
                        @focus="onFieldFocus('section-' + sIndex + '-title')"
                        @blur="onFieldBlur"
                        :style="
                            fieldFocusMap['section-' + sIndex + '-title']
                                ? {
                                      color: fieldFocusMap[
                                          'section-' + sIndex + '-title'
                                      ].color,
                                      boxShadow:
                                          '0 0 0 2px ' +
                                          fieldFocusMap[
                                              'section-' + sIndex + '-title'
                                          ].color +
                                          '33',
                                  }
                                : {}
                        "
                        :title="
                            fieldFocusMap['section-' + sIndex + '-title']
                                ? fieldFocusMap['section-' + sIndex + '-title']
                                      .name + ' is editing this title'
                                : ''
                        "
                        class="
                            bg-transparent
                            text-lg
                            font-bold
                            border-none
                            focus:ring-0
                            w-1/2
                            text-white
                            placeholder-gray-400
                        "
                        placeholder="Section Title (e.g. 1st Down, Redzone)"
                    />
                    <button
                        @click="removeSection(sIndex)"
                        class="
                            text-gray-400
                            hover:text-red-400
                            opacity-0
                            group-hover:opacity-100
                            transition-opacity
                            print:hidden
                        "
                    >
                        <ion-icon
                            name="trash-outline"
                            class="text-lg"
                        ></ion-icon>
                    </button>
                </div>

                <div class="overflow-x-auto">
                    <table class="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr
                                class="
                                    bg-gray-100
                                    text-gray-600
                                    uppercase
                                    tracking-wider
                                    text-xs
                                "
                            >
                                <th
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-12
                                        text-center
                                    "
                                >
                                    #
                                </th>
                                <th
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-20
                                        text-center
                                    "
                                >
                                    Hash
                                </th>
                                <th
                                    v-if="planType === 'Offensive'"
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-24
                                    "
                                >
                                    Pers.
                                </th>
                                <th
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-48
                                    "
                                >
                                    Formation
                                </th>
                                <th
                                    v-if="planType === 'Defensive'"
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-48
                                    "
                                >
                                    Expect/Direction
                                </th>
                                <th
                                    class="
                                        px-4
                                        py-2
                                        border-r border-gray-200
                                        w-64
                                    "
                                >
                                    Call
                                </th>
                                <th class="px-4 py-2 border-r border-gray-200">
                                    Notes / Opponent Setup
                                </th>
                                <th class="px-4 py-2 w-12 print:hidden"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(row, rIndex) in section.rows"
                                :key="rIndex"
                                class="
                                    border-b border-gray-100
                                    hover:bg-gray-50
                                    transition-colors
                                "
                            >
                                <td
                                    class="
                                        px-4
                                        py-2
                                        border-r
                                        text-center
                                        font-mono
                                        text-gray-400
                                    "
                                >
                                    {{ rIndex + 1 }}
                                </td>
                                <td class="px-4 py-2 border-r text-center">
                                    <select
                                        v-model="row.hash"
                                        @change="
                                            updateRowField(
                                                sIndex,
                                                rIndex,
                                                'hash',
                                                $event.target.value
                                            )
                                        "
                                        @focus="
                                            onFieldFocus(
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-hash'
                                            )
                                        "
                                        @blur="onFieldBlur"
                                        :style="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-hash'
                                            ]
                                                ? {
                                                      borderColor:
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-hash'
                                                          ].color,
                                                      boxShadow:
                                                          '0 0 0 2px ' +
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-hash'
                                                          ].color +
                                                          '33',
                                                  }
                                                : {}
                                        "
                                        :title="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-hash'
                                            ]
                                                ? fieldFocusMap[
                                                      'section-' +
                                                          sIndex +
                                                          '-row-' +
                                                          rIndex +
                                                          '-hash'
                                                  ].name +
                                                  ' is editing this cell'
                                                : ''
                                        "
                                        class="
                                            w-full
                                            border-none
                                            bg-transparent
                                            focus:ring-0
                                            text-xs text-center
                                            px-0
                                            text-gray-600
                                            font-bold
                                        "
                                    >
                                        <option value="">-</option>
                                        <option value="L">L</option>
                                        <option value="M">M</option>
                                        <option value="R">R</option>
                                    </select>
                                </td>
                                <td
                                    v-if="planType === 'Offensive'"
                                    class="px-4 py-2 border-r"
                                >
                                    <input
                                        v-model="row.personnel"
                                        @input="
                                            updateRowField(
                                                sIndex,
                                                rIndex,
                                                'personnel',
                                                $event.target.value
                                            )
                                        "
                                        @focus="
                                            onFieldFocus(
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-personnel'
                                            )
                                        "
                                        @blur="onFieldBlur"
                                        :style="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-personnel'
                                            ]
                                                ? {
                                                      borderColor:
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-personnel'
                                                          ].color,
                                                      boxShadow:
                                                          '0 0 0 2px ' +
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-personnel'
                                                          ].color +
                                                          '33',
                                                  }
                                                : {}
                                        "
                                        :title="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-personnel'
                                            ]
                                                ? fieldFocusMap[
                                                      'section-' +
                                                          sIndex +
                                                          '-row-' +
                                                          rIndex +
                                                          '-personnel'
                                                  ].name +
                                                  ' is editing this cell'
                                                : ''
                                        "
                                        class="
                                            w-full
                                            border-none
                                            bg-transparent
                                            focus:ring-0
                                            text-xs
                                            px-0
                                        "
                                        placeholder="10, 11..."
                                    />
                                </td>
                                <td class="px-4 py-2 border-r align-top">
                                    <textarea
                                        v-model="row.formation"
                                        @input="
                                            updateRowField(
                                                sIndex,
                                                rIndex,
                                                'formation',
                                                $event.target.value
                                            )
                                        "
                                        @focus="
                                            onFieldFocus(
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-formation'
                                            )
                                        "
                                        @blur="onFieldBlur"
                                        :style="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-formation'
                                            ]
                                                ? {
                                                      borderColor:
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-formation'
                                                          ].color,
                                                      boxShadow:
                                                          '0 0 0 2px ' +
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-formation'
                                                          ].color +
                                                          '33',
                                                  }
                                                : {}
                                        "
                                        :title="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-formation'
                                            ]
                                                ? fieldFocusMap[
                                                      'section-' +
                                                          sIndex +
                                                          '-row-' +
                                                          rIndex +
                                                          '-formation'
                                                  ].name +
                                                  ' is editing this cell'
                                                : ''
                                        "
                                        rows="1"
                                        class="
                                            w-full
                                            border-none
                                            bg-transparent
                                            focus:ring-0
                                            text-xs
                                            px-0
                                            resize-none
                                        "
                                        placeholder="Formation..."
                                    ></textarea>
                                </td>
                                <td
                                    v-if="planType === 'Defensive'"
                                    class="px-4 py-2 border-r align-top"
                                >
                                    <textarea
                                        v-model="row.expect"
                                        @input="
                                            updateRowField(
                                                sIndex,
                                                rIndex,
                                                'expect',
                                                $event.target.value
                                            )
                                        "
                                        @focus="
                                            onFieldFocus(
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-expect'
                                            )
                                        "
                                        @blur="onFieldBlur"
                                        :style="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-expect'
                                            ]
                                                ? {
                                                      borderColor:
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-expect'
                                                          ].color,
                                                      boxShadow:
                                                          '0 0 0 2px ' +
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-expect'
                                                          ].color +
                                                          '33',
                                                  }
                                                : {}
                                        "
                                        :title="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-expect'
                                            ]
                                                ? fieldFocusMap[
                                                      'section-' +
                                                          sIndex +
                                                          '-row-' +
                                                          rIndex +
                                                          '-expect'
                                                  ].name +
                                                  ' is editing this cell'
                                                : ''
                                        "
                                        rows="1"
                                        class="
                                            w-full
                                            border-none
                                            bg-transparent
                                            focus:ring-0
                                            text-xs
                                            px-0
                                            resize-none
                                        "
                                        placeholder="Expect/Direction..."
                                    ></textarea>
                                </td>
                                <td class="px-4 py-2 border-r align-top">
                                    <div class="flex flex-col gap-1">
                                        <textarea
                                            v-model="row.call"
                                            @input="
                                                updateRowField(
                                                    sIndex,
                                                    rIndex,
                                                    'call',
                                                    $event.target.value
                                                )
                                            "
                                            @focus="
                                                onFieldFocus(
                                                    'section-' +
                                                        sIndex +
                                                        '-row-' +
                                                        rIndex +
                                                        '-call'
                                                )
                                            "
                                            @blur="onFieldBlur"
                                            :style="
                                                fieldFocusMap[
                                                    'section-' +
                                                        sIndex +
                                                        '-row-' +
                                                        rIndex +
                                                        '-call'
                                                ]
                                                    ? {
                                                          borderColor:
                                                              fieldFocusMap[
                                                                  'section-' +
                                                                      sIndex +
                                                                      '-row-' +
                                                                      rIndex +
                                                                      '-call'
                                                              ].color,
                                                          boxShadow:
                                                              '0 0 0 2px ' +
                                                              fieldFocusMap[
                                                                  'section-' +
                                                                      sIndex +
                                                                      '-row-' +
                                                                      rIndex +
                                                                      '-call'
                                                              ].color +
                                                              '33',
                                                      }
                                                    : {}
                                            "
                                            :title="
                                                fieldFocusMap[
                                                    'section-' +
                                                        sIndex +
                                                        '-row-' +
                                                        rIndex +
                                                        '-call'
                                                ]
                                                    ? fieldFocusMap[
                                                          'section-' +
                                                              sIndex +
                                                              '-row-' +
                                                              rIndex +
                                                              '-call'
                                                      ].name +
                                                      ' is editing this cell'
                                                    : ''
                                            "
                                            rows="1"
                                            class="
                                                w-full
                                                border-none
                                                bg-transparent
                                                focus:ring-0
                                                text-xs
                                                font-bold
                                                px-0
                                                resize-none
                                                text-primary
                                            "
                                            placeholder="Play Call..."
                                        ></textarea>

                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                mt-1
                                                print:hidden
                                            "
                                        >
                                            <button
                                                @click="
                                                    openPlaySelector(
                                                        sIndex,
                                                        rIndex
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-blue-100
                                                    text-blue-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-blue-200
                                                    hover:bg-blue-200
                                                "
                                            >
                                                <ion-icon
                                                    name="map-outline"
                                                ></ion-icon>
                                                {{ row.plays.length || "" }}
                                                Play
                                            </button>
                                            <button
                                                @click="
                                                    openVideoSelector(
                                                        sIndex,
                                                        rIndex
                                                    )
                                                "
                                                class="
                                                    flex
                                                    items-center
                                                    gap-1
                                                    text-[10px]
                                                    bg-green-100
                                                    text-green-700
                                                    px-1.5
                                                    py-0.5
                                                    rounded
                                                    border border-green-200
                                                    hover:bg-green-200
                                                "
                                            >
                                                <ion-icon
                                                    name="videocam-outline"
                                                ></ion-icon>
                                                {{ row.videos.length || "" }}
                                                Video
                                            </button>
                                        </div>
                                        <div
                                            class="
                                                flex flex-wrap
                                                gap-1
                                                print:hidden
                                            "
                                        >
                                            <span
                                                v-for="play in row.plays"
                                                :key="play.play_id"
                                                class="
                                                    text-[9px]
                                                    bg-blue-50
                                                    text-blue-600
                                                    px-1
                                                    rounded
                                                    border border-blue-100
                                                "
                                                >{{ play.play_name }}</span
                                            >
                                            <span
                                                v-for="video in row.videos"
                                                :key="video.id"
                                                class="
                                                    text-[9px]
                                                    bg-green-50
                                                    text-green-600
                                                    px-1
                                                    rounded
                                                    border border-green-100
                                                "
                                                >{{ video.title }}</span
                                            >
                                        </div>
                                    </div>
                                </td>
                                <td class="px-4 py-2 border-r align-top">
                                    <textarea
                                        v-model="row.notes"
                                        @input="
                                            updateRowField(
                                                sIndex,
                                                rIndex,
                                                'notes',
                                                $event.target.value
                                            )
                                        "
                                        @focus="
                                            onFieldFocus(
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-notes'
                                            )
                                        "
                                        @blur="onFieldBlur"
                                        :style="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-notes'
                                            ]
                                                ? {
                                                      borderColor:
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-notes'
                                                          ].color,
                                                      boxShadow:
                                                          '0 0 0 2px ' +
                                                          fieldFocusMap[
                                                              'section-' +
                                                                  sIndex +
                                                                  '-row-' +
                                                                  rIndex +
                                                                  '-notes'
                                                          ].color +
                                                          '33',
                                                  }
                                                : {}
                                        "
                                        :title="
                                            fieldFocusMap[
                                                'section-' +
                                                    sIndex +
                                                    '-row-' +
                                                    rIndex +
                                                    '-notes'
                                            ]
                                                ? fieldFocusMap[
                                                      'section-' +
                                                          sIndex +
                                                          '-row-' +
                                                          rIndex +
                                                          '-notes'
                                                  ].name +
                                                  ' is editing this cell'
                                                : ''
                                        "
                                        rows="2"
                                        class="
                                            w-full
                                            text-xs
                                            border-none
                                            bg-transparent
                                            focus:ring-0
                                            resize-none
                                            px-0
                                        "
                                        placeholder="Notes / Coverage / Blitz..."
                                    ></textarea>
                                </td>
                                <td
                                    class="
                                        px-4
                                        py-2
                                        text-center
                                        align-middle
                                        print:hidden
                                    "
                                >
                                    <button
                                        @click="removeRow(sIndex, rIndex)"
                                        class="text-red-400 hover:text-red-600"
                                    >
                                        <ion-icon
                                            name="close-circle-outline"
                                            class="text-lg"
                                        ></ion-icon>
                                    </button>
                                </td>
                            </tr>
                            <tr class="print:hidden">
                                <td
                                    :colspan="planType === 'Offensive' ? 8 : 7"
                                    class="
                                        px-4
                                        py-2
                                        bg-gray-50
                                        text-center
                                        border-t border-gray-100
                                    "
                                >
                                    <button
                                        @click="addRow(sIndex)"
                                        class="
                                            text-xs text-primary
                                            font-bold
                                            hover:underline
                                            flex
                                            items-center
                                            justify-center
                                            gap-1
                                            w-full
                                        "
                                    >
                                        <ion-icon name="add-outline"></ion-icon>
                                        Add Row
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Footer Actions -->
            <div
                class="
                    p-6
                    bg-gray-50
                    flex
                    justify-between
                    items-center
                    border-t border-gray-200
                    print:hidden
                "
            >
                <button
                    @click="addSection()"
                    class="
                        flex
                        items-center
                        gap-2
                        bg-gray-200
                        text-gray-700
                        px-4
                        py-2
                        rounded-md
                        shadow-sm
                        hover:bg-gray-300
                        transition-colors
                        font-bold
                        text-sm
                    "
                >
                    <ion-icon
                        name="add-circle-outline"
                        class="text-xl"
                    ></ion-icon>
                    Add Section
                </button>
                <div class="flex gap-4">
                    <button
                        @click="savePlanAsNew"
                        v-if="currentPlanId"
                        class="
                            bg-blue-600
                            text-white
                            px-6
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-blue-700
                            transition-colors
                            font-bold
                        "
                    >
                        Save As New
                    </button>
                    <button
                        @click="savePlan"
                        class="
                            bg-green-600
                            text-white
                            px-6
                            py-2
                            rounded-md
                            shadow-sm
                            hover:bg-green-700
                            transition-colors
                            font-bold
                        "
                    >
                        Save Game Plan
                    </button>
                </div>
            </div>
        </div>

        <!-- Modals for Selectors -->
        <PracticePlaySelector
            v-if="activePlaySelector"
            :playbook-api="playbookApi"
            :initial-selected="
                sections[activePlaySelector.sIndex].rows[
                    activePlaySelector.rIndex
                ].plays
            "
            @close="activePlaySelector = null"
            @selected="handlePlaySelected"
        />

        <PracticeVideoSelector
            v-if="activeVideoSelector"
            :video-api="videoApi"
            :initial-selected="
                sections[activeVideoSelector.sIndex].rows[
                    activeVideoSelector.rIndex
                ].videos
            "
            @close="activeVideoSelector = null"
            @selected="handleVideoSelected"
        />

        <MiniModalCustom
            :is-open="notification.isOpen"
            :title="notification.title"
            @close="notification.isOpen = false"
        >
            <div class="py-4 text-center">
                <p class="text-sm text-gray-600">{{ notification.message }}</p>
                <div class="mt-6">
                    <button
                        @click="notification.isOpen = false"
                        class="
                            px-6
                            py-2
                            bg-primary
                            text-white
                            rounded-md
                            hover:bg-primary-dark
                            transition-colors
                            font-bold
                            text-sm
                        "
                    >
                        Okay
                    </button>
                </div>
            </div>
        </MiniModalCustom>

        <!-- Restore Deprecated Plans Modal -->
        <MiniModalCustom
            :is-open="showDeprecatedModal"
            @close="showDeprecatedModal = false"
            z-index="1001"
        >
            <div class="w-full min-w-[400px] p-4">
                <h3 class="text-lg font-bold mb-4">
                    Restore Deprecated Game Plans
                </h3>
                <div
                    v-if="deprecatedPlans.length === 0"
                    class="text-center text-gray-500 py-4 italic"
                >
                    No deprecated plans found
                </div>
                <ul v-else class="divide-y max-h-[400px] overflow-y-auto">
                    <li
                        v-for="p in deprecatedPlans"
                        :key="p.id"
                        class="py-3 flex justify-between items-center"
                    >
                        <div class="text-sm">
                            <span class="font-bold">{{
                                p.opponent || "Unnamed"
                            }}</span>
                            <span class="ml-2 text-gray-400">{{
                                p.game_date
                                    ? getLocalDateString(p.game_date)
                                    : "No date"
                            }}</span>
                        </div>
                        <button
                            @click="restorePlan(p.id)"
                            class="
                                text-xs
                                bg-primary
                                text-white
                                px-3
                                py-1
                                rounded
                                hover:bg-primary-dark
                                transition-colors
                            "
                        >
                            Restore
                        </button>
                    </li>
                </ul>
            </div>
        </MiniModalCustom>
    </div>
    <div class="mx-auto text-center py-12" v-else>
        <ion-icon
            name="lock-closed-outline"
            class="text-6xl text-gray-300 mb-4"
        ></ion-icon>
        <h2 class="text-2xl font-bold text-gray-700">Access Denied</h2>
        <p class="text-gray-500 mt-2">
            You do not have the required tags to view Game Plans.
        </p>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import PracticePlaySelector from "./PracticePlaySelector.vue";
import PracticeVideoSelector from "./PracticeVideoSelector.vue";
import MiniModalCustom from "../MiniModalCustom.vue";

const props = defineProps({
    team: Object,
    availableTags: { type: Array, default: () => [] },
    initialPlans: { type: Array, default: () => [] },
    deprecatedPlans: { type: Array, default: () => [] },
    hasAccess: { type: Boolean, default: true },
    canManageAccess: { type: Boolean, default: false },
    currentAccessTags: { type: Array, default: () => [] },
    currentUserName: { type: String, default: "User" },
    websocketUrl: { type: String, default: "ws://localhost" },
    env: { type: String, default: "local" },
});

const getLocalDateString = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isDark = (color) => {
    if (!color) return false;
    color = color.replace("#", "");
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq < 128;
};

const isToday = (dateString) => {
    if (!dateString) return false;
    return dateString.split("T")[0] === getLocalDateString(new Date());
};

const activeTags = ref({});
const isAccessControlOpen = ref(false);
const plansList = ref([]);
const deprecatedPlans = ref([]);
const showDeprecatedModal = ref(false);

const globalActiveTags = ref(
    props.availableTags.reduce((acc, tag) => {
        if (
            props.currentAccessTags &&
            props.currentAccessTags.includes(tag.id)
        ) {
            acc[tag.id] = true;
        }
        return acc;
    }, {})
);

const notification = ref({
    isOpen: false,
    title: "",
    message: "",
});

const showAlert = (title, message) => {
    notification.value = {
        isOpen: true,
        title,
        message,
    };
};

const saveGlobalAccess = async () => {
    const selectedTags = Object.keys(globalActiveTags.value)
        .filter((k) => globalActiveTags.value[k])
        .map(Number);
    try {
        await axios.post("/game_plans/update-access?teamId=" + props.team.id, {
            tags: selectedTags,
        });
        showAlert("Success", "Global access permissions updated!");
    } catch (e) {
        showAlert("Error", "Failed to update permissions.");
    }
};

const playbookApi = "/playbook/list";
const videoApi = "/video/list";

const planType = ref("Offensive");
const opponent = ref("");
const gameDate = ref(getLocalDateString(new Date()));

const sections = ref([]);
const connectedUsers = ref([]);
const fieldFocusMap = ref({});

const activePlaySelector = ref(null);
const activeVideoSelector = ref(null);

const createEmptyRow = () => ({
    hash: "",
    personnel: "",
    formation: "",
    expect: "",
    call: "",
    notes: "",
    plays: [],
    videos: [],
});

const defaultOffensiveSections = [
    "Openers",
    "1st Down",
    "2nd Down",
    "3rd & Short",
    "3rd & Long",
    "Red Zone",
    "Goal Line",
];
const defaultDefensiveSections = [
    "Top Runs",
    "Top Passes",
    "1st Down",
    "2nd Down",
    "3rd & Short",
    "3rd & Long",
    "Red Zone",
    "Goal Line",
];

// Y.js collaboration setup
let ydoc = null;
let provider = null;
let ysections = null;
let ymeta = null;

const initYjs = (planId) => {
    if (!planId) return;

    if (provider) {
        provider.destroy();
    }
    if (ydoc) {
        ydoc.destroy();
    }

    ydoc = new Y.Doc();

    provider = new WebsocketProvider(
        props.websocketUrl,
        `practiceplan-${props.env}-${planId}`,
        ydoc
    );

    ymeta = ydoc.getMap("meta");
    ysections = ydoc.getArray("sections");

    ymeta.observe(() => {
        if (ymeta.get("planType") !== undefined)
            planType.value = ymeta.get("planType");
        if (ymeta.get("opponent") !== undefined)
            opponent.value = ymeta.get("opponent");
        if (ymeta.get("gameDate") !== undefined)
            gameDate.value = ymeta.get("gameDate");
    });

    ysections.observeDeep((events, transaction) => {
        if (transaction.local) return;

        sections.value = ysections.toArray().map((ysec) => {
            return ysec.toJSON();
        });
    });

    // Awareness Setup
    const colors = [
        "#E02424",
        "#3F83F8",
        "#0E9F6E",
        "#D03801",
        "#9061F9",
        "#6B7280",
        "#EC4899",
        "#059669",
    ];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const username = props.currentUserName || "User";

    provider.awareness.setLocalStateField("user", {
        name: username,
        color: randomColor,
    });

    provider.awareness.on("change", () => {
        const states = Array.from(provider.awareness.getStates().values());
        connectedUsers.value = states
            .filter((state) => state.user)
            .map((state) => state.user);

        const focusMap = {};
        Array.from(provider.awareness.getStates().entries()).forEach(
            ([clientId, state]) => {
                if (clientId === provider.awareness.clientID) return;
                if (state.user && state.focus) {
                    focusMap[state.focus.path] = {
                        name: state.user.name,
                        color: state.user.color,
                    };
                }
            }
        );
        fieldFocusMap.value = focusMap;
    });

    provider.on("sync", (isSynced) => {
        if (isSynced) {
            if (ysections.length === 0 && sections.value.length > 0) {
                ydoc.transact(() => {
                    ymeta.set("planType", planType.value);
                    ymeta.set("opponent", opponent.value);
                    ymeta.set("gameDate", gameDate.value);

                    sections.value.forEach((sec) => {
                        const ysec = new Y.Map();
                        ysec.set("title", sec.title);

                        const yrows = new Y.Array();
                        sec.rows.forEach((row) => {
                            const yrow = new Y.Map();
                            yrow.set("hash", row.hash || "");
                            yrow.set("personnel", row.personnel || "");
                            yrow.set("formation", row.formation || "");
                            yrow.set("expect", row.expect || "");
                            yrow.set("call", row.call || "");
                            yrow.set("notes", row.notes || "");
                            yrow.set("plays", row.plays || []);
                            yrow.set("videos", row.videos || []);
                            yrows.push([yrow]);
                        });
                        ysec.set("rows", yrows);
                        ysections.push([ysec]);
                    });
                });
            } else if (ysections.length > 0) {
                planType.value = ymeta.get("planType") || planType.value;
                opponent.value = ymeta.get("opponent") || opponent.value;
                gameDate.value = ymeta.get("gameDate") || gameDate.value;

                sections.value = ysections.toArray().map((ysec) => {
                    return ysec.toJSON();
                });
            }
        }
    });
};

const onFieldFocus = (path) => {
    if (provider) {
        provider.awareness.setLocalStateField("focus", { path });
    }
};

const onFieldBlur = () => {
    if (provider) {
        provider.awareness.setLocalStateField("focus", null);
    }
};

const updatePlanType = (val) => {
    if (ymeta) {
        ymeta.set("planType", val);
    }
};

const updateOpponent = (val) => {
    if (ymeta) {
        ymeta.set("opponent", val);
    }
};

const updateGameDate = (val) => {
    if (ymeta) {
        ymeta.set("gameDate", val);
    }
};

const updateSectionTitle = (sIndex, title) => {
    if (ysections && sIndex < ysections.length) {
        const ysec = ysections.get(sIndex);
        ysec.set("title", title);
    }
};

const updateRowField = (sIndex, rIndex, field, value) => {
    if (ysections && sIndex < ysections.length) {
        const ysec = ysections.get(sIndex);
        const yrows = ysec.get("rows");
        if (yrows && rIndex < yrows.length) {
            const yrow = yrows.get(rIndex);
            yrow.set(field, value);
        }
    }
};

const createNewPlan = () => {
    currentPlanId.value = null;
    planType.value = "Offensive";
    opponent.value = "";
    gameDate.value = getLocalDateString(new Date());
    sections.value = [];
    const initialSections = defaultOffensiveSections.slice(0, 3);
    initialSections.forEach((title) => addSection(title));
};

const addSection = (title = "") => {
    const newSec = {
        title: title || "New Section",
        rows: [createEmptyRow()],
    };
    sections.value.push(newSec);

    if (ysections) {
        const ysec = new Y.Map();
        ysec.set("title", newSec.title);
        const yrows = new Y.Array();
        const yrow = new Y.Map();
        yrow.set("hash", "");
        yrow.set("personnel", "");
        yrow.set("formation", "");
        yrow.set("expect", "");
        yrow.set("call", "");
        yrow.set("notes", "");
        yrow.set("plays", []);
        yrow.set("videos", []);
        yrows.push([yrow]);
        ysec.set("rows", yrows);

        ysections.push([ysec]);
    }
};

const removeSection = (sIndex) => {
    if (confirm("Are you sure you want to remove this section?")) {
        sections.value.splice(sIndex, 1);
        if (ysections && sIndex < ysections.length) {
            ysections.delete(sIndex, 1);
        }
    }
};

const addRow = (sIndex) => {
    const newRow = createEmptyRow();
    sections.value[sIndex].rows.push(newRow);

    if (ysections && sIndex < ysections.length) {
        const ysec = ysections.get(sIndex);
        const yrows = ysec.get("rows");
        const yrow = new Y.Map();
        yrow.set("hash", "");
        yrow.set("personnel", "");
        yrow.set("formation", "");
        yrow.set("expect", "");
        yrow.set("call", "");
        yrow.set("notes", "");
        yrow.set("plays", []);
        yrow.set("videos", []);
        yrows.push([yrow]);
    }
};

const removeRow = (sIndex, rIndex) => {
    sections.value[sIndex].rows.splice(rIndex, 1);
    if (ysections && sIndex < ysections.length) {
        const ysec = ysections.get(sIndex);
        const yrows = ysec.get("rows");
        if (yrows && rIndex < yrows.length) {
            yrows.delete(rIndex, 1);
        }
    }
};

const openPlaySelector = (sIndex, rIndex) => {
    activePlaySelector.value = { sIndex, rIndex };
};

const openVideoSelector = (sIndex, rIndex) => {
    activeVideoSelector.value = { sIndex, rIndex };
};

const handlePlaySelected = (selectedPlays) => {
    if (activePlaySelector.value) {
        const { sIndex, rIndex } = activePlaySelector.value;
        sections.value[sIndex].rows[rIndex].plays = selectedPlays;
        updateRowField(sIndex, rIndex, "plays", selectedPlays);
        activePlaySelector.value = null;
    }
};

const handleVideoSelected = (selectedVideos) => {
    if (activeVideoSelector.value) {
        const { sIndex, rIndex } = activeVideoSelector.value;
        sections.value[sIndex].rows[rIndex].videos = selectedVideos;
        updateRowField(sIndex, rIndex, "videos", selectedVideos);
        activeVideoSelector.value = null;
    }
};

const currentPlanId = ref(null);

const printPlan = () => {
    window.print();
};

const exportToExcel = () => {
    let container = document.getElementById("printable-plan").cloneNode(true);
    let tables = container.querySelectorAll("table");

    if (tables.length === 0) return;

    let masterTable = document.createElement("table");

    const addHeaderRow = (text, colSpan = 8, isTitle = false) => {
        let row = masterTable.insertRow();
        let cell = row.insertCell();
        cell.colSpan = colSpan;
        cell.innerHTML = isTitle
            ? `<b style="font-size:18px;">${text}</b>`
            : `<b>${text}</b>`;
    };

    addHeaderRow(`${props.team.name} Game Plan`, 8, true);
    addHeaderRow(`Type: ${planType.value || ""} Plan`);
    addHeaderRow(`Opponent: ${opponent.value || ""}`);
    addHeaderRow(`Game Date: ${gameDate.value || ""}`);

    let spacerRow1 = masterTable.insertRow();
    spacerRow1.insertCell().colSpan = 8;

    tables.forEach((table, index) => {
        let sectionTitle =
            container.querySelectorAll(".bg-gray-800 input")[index]?.value ||
            "Section";
        let titleRow = masterTable.insertRow();
        let titleCell = titleRow.insertCell();
        titleCell.colSpan = 8;
        titleCell.innerHTML = `<b>${sectionTitle}</b>`;
        titleCell.style.backgroundColor = "#1f2937";
        titleCell.style.color = "#ffffff";
        titleCell.style.fontSize = "16px";
        titleCell.style.padding = "10px";

        let hiddenElements = table.querySelectorAll(".print\\:hidden");
        hiddenElements.forEach((el) => el.parentNode.removeChild(el));

        let inputs = table.querySelectorAll("input, textarea, select");
        inputs.forEach((input) => {
            let text = document.createTextNode(input.value || "");
            input.parentNode.replaceChild(text, input);
        });

        let rows = table.querySelectorAll("tr");
        rows.forEach((row) => masterTable.appendChild(row.cloneNode(true)));

        let spacerRow = masterTable.insertRow();
        let spacerCell = spacerRow.insertCell();
        spacerCell.colSpan = 8;
        spacerCell.style.height = "20px";
    });

    let html = masterTable.outerHTML;
    let template =
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><!--[if gte gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Plan</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>{table}</body></html>';
    let fullHtml = template.replace("{table}", html);

    let blob = new Blob([fullHtml], { type: "application/vnd.ms-excel" });
    let url = URL.createObjectURL(blob);

    let link = document.createElement("a");
    link.href = url;
    link.download = `${props.team.name
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}_game_plan.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const savePlanAsNew = () => {
    currentPlanId.value = null;
    savePlan();
};

const savePlan = async () => {
    const payload = {
        id: currentPlanId.value,
        plan_type: planType.value,
        opponent: opponent.value,
        game_date: gameDate.value,
        sections: sections.value,
    };

    try {
        const res = await axios.post(
            "/game_plans?teamId=" + props.team.id,
            payload
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            const updated = plansList.value.find(
                (p) => p.id === res.data.saved_id
            );
            if (updated) loadPlan(updated);
            showAlert("Success", "Game plan saved successfully!");
        }
    } catch (e) {
        console.error(e);
        showAlert("Error", "Failed to save.");
    }
};

const loadPlan = (plan) => {
    currentPlanId.value = plan.id;
    planType.value = plan.plan_type || "Offensive";
    opponent.value = plan.opponent || "";
    gameDate.value = plan.game_date
        ? plan.game_date.split("T")[0]
        : getLocalDateString(new Date());
    sections.value = plan.sections || [];

    initYjs(plan.id);

    window.scrollTo({
        top: document.getElementById("printable-plan").offsetTop - 100,
        behavior: "smooth",
    });
};

const deletePlan = async (id) => {
    if (!confirm("Are you sure you want to deprecate this game plan?")) return;
    try {
        const res = await axios.delete(
            `/game_plans/${id}?teamId=${props.team.id}`
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            if (currentPlanId.value == id) {
                createNewPlan();
            }
        }
    } catch (e) {
        showAlert("Error", "Failed to deprecate.");
    }
};

const restorePlan = async (id) => {
    try {
        const res = await axios.post(
            `/game_plans/${id}/restore?teamId=${props.team.id}`
        );
        if (res.data.success) {
            plansList.value = res.data.plans;
            deprecatedPlans.value = res.data.deprecatedPlans;
            if (deprecatedPlans.value.length === 0) {
                showDeprecatedModal.value = false;
            }
            showAlert("Success", "Plan restored!");
        }
    } catch (e) {
        showAlert("Error", "Failed to restore.");
    }
};

onMounted(() => {
    plansList.value = props.initialPlans || [];
    deprecatedPlans.value = props.deprecatedPlans || [];

    const urlParams = new URLSearchParams(window.location.search);
    const planIdParam = urlParams.get("planId");

    if (planIdParam) {
        const targetPlan = plansList.value.find((p) => p.id == planIdParam);
        if (targetPlan) {
            loadPlan(targetPlan);
            return;
        }
    }

    if (plansList.value.length > 0) {
        loadPlan(plansList.value[0]);
    } else {
        createNewPlan();
    }
});

onBeforeUnmount(() => {
    if (provider) {
        provider.destroy();
    }
    if (ydoc) {
        ydoc.destroy();
    }
});
</script>

<style scoped>
.bg-primary {
    background-color: #1b6fc3;
}
.bg-primary-dark {
    background-color: #155594;
}
.text-primary {
    color: #1b6fc3;
}
</style>

<style>
@media print {
    body * {
        visibility: hidden;
    }
    #printable-plan,
    #printable-plan * {
        visibility: visible;
    }
    #printable-plan {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        box-shadow: none;
        border: none;
    }
    @page {
        size: landscape;
        margin: 1cm;
    }
    /* Hide placeholders */
    ::placeholder {
        color: transparent !important;
    }
    /* Remove borders from inputs and textareas */
    input,
    textarea {
        border: none !important;
        box-shadow: none !important;
        resize: none !important;
        background: transparent !important;
        outline: none !important;
    }
    /* Hide select dropdown arrows and borders */
    select {
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        background-image: none !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        outline: none !important;
    }
}
</style>
